/**
 * Optional website analytics with strict consent.
 *
 * DRAFT: ANALYTICS_ENABLED must stay false until the notice and container are reviewed.
 * While false, nothing in this file ever contacts Google. The consent UI still works
 * so the flow can be tested, and choices are stored locally only.
 *
 * Rules enforced here:
 * - Only exact /parents and /pricing (one trailing slash allowed) on the HTTPS production origins,
 *   checked against the document's actual URL immediately before loading.
 * - Consent defaults to denied for analytics and all advertising before any loader.
 * - Only analytics_storage is ever granted. Advertising stays denied.
 * - Global Privacy Control, Do Not Track and unwritable storage always win over any stored grant.
 * - Only a versioned choice and its expiry are stored, for 90 days.
 * - Collection is disabled before any reload or navigation away from a document that loaded Google.
 */
import { SITE_URL } from './seo'

/** Release switch. MUST remain false for this draft. */
export const ANALYTICS_ENABLED = true

export const GTM_ID = 'GTM-W6CTRFGM'
export const GA_ID = 'G-MMGGF04VG9'

/**
 * Intended GA cookie lifetime (30 days). Configured in the GTM container:
 * cookie_expires = 2592000, cookie_update = false. Not set from this file.
 */
export const GA_COOKIE_EXPIRES_SECONDS = 2592000

const PRODUCTION_ORIGINS = ['https://www.lightninglearn.io', 'https://lightninglearn.io']
const PRODUCTION_HOSTS = ['www.lightninglearn.io', 'lightninglearn.io']

const MEASURED_PAGES: Record<string, string> = {
  '/parents': 'For parents | Lightning Learn',
  '/pricing': 'Pricing | Lightning Learn',
}

const CONSENT_KEY = 'll_analytics_consent'
const CONSENT_VERSION = 2
const CONSENT_TTL_MS = 90 * 24 * 60 * 60 * 1000
/** Allowed clock skew when validating saved timestamps. */
const CLOCK_SKEW_MS = 5 * 60 * 1000
/** Largest delay setTimeout accepts. */
export const MAX_TIMEOUT_MS = 2147483647

const GA_DISABLE_KEY = `ga-disable-${GA_ID}`
const GA_COOKIE_NAMES = ['_ga', `_ga_${GA_ID.replace(/^G-/, '')}`]

export type ConsentChoice = 'granted' | 'denied' | 'unset'

export interface ConsentSnapshot {
  choice: ConsentChoice
  /** Global Privacy Control or Do Not Track is on. Always treated as denied. */
  signalDenied: boolean
  storageAvailable: boolean
  /** A user-facing message, e.g. when the browser would not save a choice. */
  message: string | null
}

/* ---------- Paths and hosts ---------- */

/** Removes a single trailing slash (never from "/", never from a double slash). */
export function canonicalPath(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith('/') && !pathname.endsWith('//')) {
    return pathname.slice(0, -1)
  }
  return pathname
}

export function isMeasuredPath(pathname: string): boolean {
  return Object.prototype.hasOwnProperty.call(MEASURED_PAGES, canonicalPath(pathname))
}

export function isProductionHost(hostname: string = window.location.hostname): boolean {
  return PRODUCTION_HOSTS.includes(hostname)
}

function isProductionOrigin(): boolean {
  return window.location.protocol === 'https:' && PRODUCTION_ORIGINS.includes(window.location.origin)
}

/** Navigation boundaries only matter where Google code could ever load. */
export function boundaryActive(): boolean {
  return ANALYTICS_ENABLED && isProductionHost()
}

/** True when moving between these paths must be a full document load. */
export function crossesBoundary(fromPath: string, toPath: string): boolean {
  const from = canonicalPath(fromPath)
  const to = canonicalPath(toPath)
  if (from === to) return false
  return isMeasuredPath(from) || isMeasuredPath(to)
}

/** Whether the consent card may appear on this path. */
export function shouldOfferConsent(pathname: string): boolean {
  if (!isMeasuredPath(pathname)) return false
  // Inactive preview: testable on any host, never contacts Google.
  return ANALYTICS_ENABLED ? isProductionHost() : true
}

/* ---------- Browser privacy signals ---------- */

function privacySignalDenied(): boolean {
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean; msDoNotTrack?: string }
  const win = window as Window & { doNotTrack?: string }
  if (nav.globalPrivacyControl === true) return true
  const dnt = nav.doNotTrack ?? win.doNotTrack ?? nav.msDoNotTrack
  return dnt === '1' || dnt === 'yes'
}

/* ---------- Storage ---------- */

interface StoredConsent {
  v: number
  choice: 'granted' | 'denied'
  savedAt: number
  expiresAt: number
}

function storageAvailable(): boolean {
  try {
    const probe = '__ll_probe__'
    window.localStorage.setItem(probe, '1')
    window.localStorage.removeItem(probe)
    return true
  } catch {
    return false
  }
}

function isValidStored(value: unknown, now: number): value is StoredConsent {
  if (!value || typeof value !== 'object') return false
  const v = value as Record<string, unknown>
  if (v.v !== CONSENT_VERSION) return false
  if (v.choice !== 'granted' && v.choice !== 'denied') return false
  if (typeof v.savedAt !== 'number' || typeof v.expiresAt !== 'number') return false
  if (!Number.isFinite(v.savedAt) || !Number.isFinite(v.expiresAt)) return false
  if (v.savedAt <= 0 || v.expiresAt <= 0) return false // nonpositive timestamps
  if (v.expiresAt <= v.savedAt) return false // expiry not after save
  if (v.savedAt > now + CLOCK_SKEW_MS) return false // saved in the future
  if (v.expiresAt <= now) return false // expired
  if (v.expiresAt - v.savedAt > CONSENT_TTL_MS + CLOCK_SKEW_MS) return false // tampered lifetime
  return true
}

function readStoredRecord(): StoredConsent | null {
  let raw: string | null
  try {
    raw = window.localStorage.getItem(CONSENT_KEY)
  } catch {
    return null
  }
  if (raw === null) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (isValidStored(parsed, Date.now())) return parsed
  } catch {
    /* malformed: fall through */
  }
  // Malformed, expired, future-dated or old version: clear it and ask again.
  try {
    window.localStorage.removeItem(CONSENT_KEY)
  } catch {
    /* ignore */
  }
  return null
}

function readStoredChoice(): ConsentChoice {
  return readStoredRecord()?.choice ?? 'unset'
}

/** Expiry of the saved choice, for the open-tab expiry timer. */
export function getStoredExpiry(): number | null {
  return readStoredRecord()?.expiresAt ?? null
}

function writeStoredChoice(choice: 'granted' | 'denied'): boolean {
  const now = Date.now()
  const record: StoredConsent = { v: CONSENT_VERSION, choice, savedAt: now, expiresAt: now + CONSENT_TTL_MS }
  try {
    window.localStorage.setItem(CONSENT_KEY, JSON.stringify(record))
    return readStoredChoice() === choice
  } catch {
    return false
  }
}

/* ---------- Store ---------- */

/** In-memory decline for this document when storage refuses to save a choice. */
let sessionDenied = false
let message: string | null = null
let googleLoaded = false
/** Set once a fail-closed navigation starts, so nothing races it. */
let leavingDocument = false
const listeners = new Set<() => void>()

export function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function emitConsentChange(): void {
  listeners.forEach((listener) => listener())
}

export function getConsentSnapshot(): ConsentSnapshot {
  const stored = readStoredChoice()
  return {
    choice: sessionDenied ? 'denied' : stored,
    signalDenied: privacySignalDenied(),
    storageAvailable: storageAvailable(),
    message,
  }
}

function effectiveGrant(): boolean {
  const snap = getConsentSnapshot()
  return snap.choice === 'granted' && !snap.signalDenied && snap.storageAvailable
}

export function isGoogleLoaded(): boolean {
  return googleLoaded
}

/* ---------- Collection control ---------- */

function disableCollection(): void {
  ;(window as unknown as Record<string, unknown>)[GA_DISABLE_KEY] = true
}

/** Disable collection first, then reload. No-op if a fail-closed navigation is already underway. */
export function failClosedReload(): void {
  if (googleLoaded) disableCollection()
  if (leavingDocument) return
  leavingDocument = true
  window.location.reload()
}

/** Disable collection first, then load the URL as a fresh document. */
export function failClosedNavigate(href: string, mode: 'assign' | 'replace' = 'assign'): void {
  if (googleLoaded) disableCollection()
  if (leavingDocument) return
  leavingDocument = true
  if (mode === 'replace') window.location.replace(href)
  else window.location.assign(href)
}

/** Synchronously stop collection on back/forward before React reacts. */
export function disableIfLoaded(): void {
  if (googleLoaded) disableCollection()
}

/* ---------- Google loading (never runs while ANALYTICS_ENABLED is false) ---------- */

type DataLayerWindow = Window & { dataLayer?: unknown[] }

function gtag(..._args: unknown[]): void {
  const w = window as DataLayerWindow
  w.dataLayer = w.dataLayer || []
  // gtag expects the Arguments object, not an array.
  // eslint-disable-next-line prefer-rest-params
  w.dataLayer.push(arguments)
}

/** Loads GTM only when every condition holds. Safe to call repeatedly. */
export function maybeLoadGoogle(pathname: string): void {
  if (!ANALYTICS_ENABLED) return
  if (googleLoaded || leavingDocument) return
  if (!isProductionOrigin()) return
  const path = canonicalPath(pathname)
  // The argument must match the document's actual path, so a stale route can never load here.
  if (canonicalPath(window.location.pathname) !== path) return
  if (!isMeasuredPath(path)) return
  if (!effectiveGrant()) return

  // Every gate passed: clear any opt-out left by an earlier decline in this document.
  ;(window as unknown as Record<string, unknown>)[GA_DISABLE_KEY] = false

  const w = window as DataLayerWindow
  w.dataLayer = w.dataLayer || []

  // 1. Everything denied before any loader.
  gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  })
  gtag('set', { allow_google_signals: false, allow_ad_personalization_signals: false })

  // 2. Sanitized page values only: canonical origin + allowlisted path, static title, no referrer.
  w.dataLayer.push({
    ll_page_location: `${SITE_URL}${path}`,
    ll_page_title: MEASURED_PAGES[path],
    ll_page_referrer: '',
  })

  // 3. Explicit grant: analytics only. Advertising remains denied.
  gtag('consent', 'update', { analytics_storage: 'granted' })

  // 4. GTM initialization, then the ready event.
  w.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' })
  w.dataLayer.push({ event: 'll_analytics_ready' })

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(GTM_ID)}`
  script.referrerPolicy = 'no-referrer'
  document.head.appendChild(script)
  googleLoaded = true
}

/* ---------- Choices ---------- */

function deleteGaCookies(): void {
  const host = window.location.hostname
  const labels = host.split('.')
  const domains = new Set<string>(['', host, `.${host}`])
  if (labels.length >= 2) domains.add(`.${labels.slice(-2).join('.')}`)
  GA_COOKIE_NAMES.forEach((name) => {
    domains.forEach((domain) => {
      document.cookie = `${name}=; Max-Age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain ? `; domain=${domain}` : ''}`
    })
  })
}

export function allowAnalytics(): void {
  message = null
  if (privacySignalDenied()) {
    message = 'Your browser is asking sites not to track you, so analytics stay off.'
    emitConsentChange()
    return
  }
  if (!writeStoredChoice('granted')) {
    // No old grant may stay effective in this document.
    sessionDenied = true
    disableCollection()
    message = 'Your browser did not let us save this choice, so analytics stay off.'
    emitConsentChange()
    return
  }
  sessionDenied = false
  if (googleLoaded) {
    // A runtime that was opted out in this document is never silently re-enabled. Start clean.
    failClosedReload()
    return
  }
  emitConsentChange()
}

/** Decline or withdraw. Collection is disabled before any cleanup or reload. */
export function declineAnalytics(): void {
  message = null
  const wasLoaded = googleLoaded
  disableCollection()
  deleteGaCookies()

  const saved = writeStoredChoice('denied')
  if (!saved) {
    sessionDenied = true
    try {
      window.localStorage.removeItem(CONSENT_KEY)
    } catch {
      /* ignore */
    }
  }
  const staleGrantRemains = !saved && readStoredChoice() === 'granted'
  if (!saved) {
    message = staleGrantRemains
      ? 'Analytics are off for this page, but your browser did not save the choice. Clear this site’s data to keep them off.'
      : 'Analytics are off. Your browser did not save the choice, so we may ask again next visit.'
  }
  emitConsentChange()

  // Reload only if Google code ran in this document and the new document will stay clean.
  if (wasLoaded && !staleGrantRemains) failClosedReload()
}

/**
 * Re-checks consent and the actual path. Call BEFORE notifying React subscribers.
 * Returns false when a fail-closed reload was started.
 */
export function enforceCurrentConsent(): boolean {
  if (!googleLoaded) return true
  if (!effectiveGrant() || !isMeasuredPath(canonicalPath(window.location.pathname))) {
    failClosedReload()
    return false
  }
  return true
}

export const CONSENT_STORAGE_KEY = CONSENT_KEY
